import React, { useState, useEffect } from 'react';
import { CreditCard, Plus, Trash2, Star, AlertCircle, CheckCircle } from 'lucide-react';
import { subscriptionAPI, PaymentMethod } from '../../services/subscriptionAPI';

interface CardManagementProps {
  siteId: string;
}

const CardManagement: React.FC<CardManagementProps> = ({ siteId }) => {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [showAddCard, setShowAddCard] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadPaymentMethods();
  }, [siteId]);

  const loadPaymentMethods = async () => {
    try {
      setLoading(true);
      const response = await subscriptionAPI.getPaymentMethods(siteId);
      setPaymentMethods(response.data.paymentMethods);
      setDefaultPaymentMethodId(response.data.defaultPaymentMethodId || '');
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to load payment methods');
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefault = async (paymentMethodId: string) => {
    try {
      setProcessing(true);
      await subscriptionAPI.setDefaultPaymentMethod(siteId, { paymentMethodId });
      setDefaultPaymentMethodId(paymentMethodId);
      setSuccess('Default payment method updated successfully');
      loadPaymentMethods();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to set default payment method');
    } finally {
      setProcessing(false);
    }
  };

  const handleRemoveCard = async (paymentMethodId: string) => {
    if (!window.confirm('Are you sure you want to remove this payment method?')) {
      return;
    }

    try {
      setProcessing(true);
      await subscriptionAPI.removePaymentMethod(siteId, paymentMethodId);
      setSuccess('Payment method removed successfully');
      loadPaymentMethods();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to remove payment method');
    } finally {
      setProcessing(false);
    }
  };

  const getCardBrandIcon = (brand: string) => {
    const brandLower = brand?.toLowerCase();
    switch (brandLower) {
      case 'visa':
        return '💳';
      case 'mastercard':
        return '💳';
      case 'amex':
        return '💳';
      case 'discover':
        return '💳';
      default:
        return '💳';
    }
  };

  const getCardColor = (brand: string) => {
    const brandLower = brand?.toLowerCase();
    switch (brandLower) {
      case 'visa':
        return 'bg-blue-100 text-blue-800';
      case 'mastercard':
        return 'bg-red-100 text-red-800';
      case 'amex':
        return 'bg-blue-100 text-blue-800';
      case 'discover':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Payment Methods</h3>
          <p className="text-gray-600">Manage your payment cards for subscription billing</p>
        </div>
        <button
          onClick={() => setShowAddCard(true)}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Add Card</span>
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <p className="text-green-800">{success}</p>
          </div>
        </div>
      )}

      {/* Payment Methods List */}
      {paymentMethods.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
          <CreditCard className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Payment Methods</h3>
          <p className="text-gray-600 mb-4">
            Add a payment method to enable subscription billing
          </p>
          <button
            onClick={() => setShowAddCard(true)}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center space-x-2 mx-auto"
          >
            <Plus className="w-4 h-4" />
            <span>Add Your First Card</span>
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {paymentMethods.map((method) => (
            <div
              key={method.stripePaymentMethodId}
              className="bg-white rounded-lg shadow-sm border p-6"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className={`p-3 rounded-lg ${getCardColor(method.brand || '')}`}>
                    <span className="text-2xl">{getCardBrandIcon(method.brand || '')}</span>
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <p className="font-medium text-gray-900 capitalize">
                        {method.brand || 'Card'} ending in {method.last4}
                      </p>
                      {method.isDefault && (
                        <span className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">
                          <Star className="w-3 h-3 mr-1" />
                          Default
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">
                      Expires {method.expMonth}/{method.expYear} • {method.funding}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {!method.isDefault && (
                    <button
                      onClick={() => handleSetDefault(method.stripePaymentMethodId)}
                      disabled={processing}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                      Set as Default
                    </button>
                  )}
                  {paymentMethods.length > 1 && (
                    <button
                      onClick={() => handleRemoveCard(method.stripePaymentMethodId)}
                      disabled={processing}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Card Modal */}
      {showAddCard && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add Payment Method</h3>
              
              {/* Stripe Elements will go here */}
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <CreditCard className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                <p className="text-gray-600 text-sm">
                  Stripe payment form will be integrated here
                </p>
                <p className="text-gray-500 text-xs mt-1">
                  This is a placeholder for the Stripe Elements integration
                </p>
              </div>

              <div className="flex justify-end space-x-4 mt-6">
                <button
                  onClick={() => setShowAddCard(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    // This would integrate with Stripe Elements
                    alert('Stripe integration would be completed here');
                    setShowAddCard(false);
                  }}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Add Card
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CardManagement;
